// app/(app)/reports/stock-addition.tsx
import { Button } from "@/components/default/forms";
import DatePicker from "@/components/default/forms/DatePicker";
import SearchableSelect from "@/components/default/forms/SearchableSelect";
import { useAppTheme } from "@/hooks/useAppTheme";
import { reportsApi } from "@/services/reportsApi";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { fetchProducts } from "@/store/slices/productSlice";
import { showAlert } from "@/utils/alert";
import { Ionicons } from "@expo/vector-icons";
import * as FileSystem from "expo-file-system/legacy";
import { useFocusEffect, useRouter } from "expo-router";
import * as Sharing from "expo-sharing";
import { useCallback, useEffect, useState } from "react";
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";

export default function StockAdditionReportScreen() {
  const { colors, spacing } = useAppTheme();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const { products } = useAppSelector((state) => state.products);
  const { loading: productsLoading } = useAppSelector(
    (state) => state.products,
  );

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [selectedProductId, setSelectedProductId] = useState<string>("");
  const [downloading, setDownloading] = useState(false);

  // Fetch products on mount
  useEffect(() => {
    dispatch(fetchProducts({ page: 1, limit: 1000, search: "" }));
  }, []);

  // Reset filters on focus
  useFocusEffect(
    useCallback(() => {
      setStartDate("");
      setEndDate("");
      setSelectedProductId("");
    }, []),
  );

  const handleDownloadPdf = async () => {
    setDownloading(true);
    try {
      const params: any = {};
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (selectedProductId) params.productId = parseInt(selectedProductId);
      const blob = await reportsApi.downloadStockAdditionReportPdf(params);

      if (Platform.OS === "web") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
        setTimeout(() => URL.revokeObjectURL(url), 100);
      } else {
        const reader = new FileReader();
        reader.onload = async () => {
          const base64 = (reader.result as string).split(",")[1];
          const fileUri =
            FileSystem.cacheDirectory +
            `stock_addition_report_${Date.now()}.pdf`;
          await FileSystem.writeAsStringAsync(fileUri, base64, {
            encoding: FileSystem.EncodingType.Base64,
          });
          if (await Sharing.isAvailableAsync()) {
            await Sharing.shareAsync(fileUri);
          } else {
            showAlert("Error", "Sharing not available");
          }
        };
        reader.readAsDataURL(blob);
      }
    } catch (error: any) {
      showAlert("Error", error.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  const styles = StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },
    keyboardAvoid: { flex: 1 },
    scrollContent: { padding: spacing.lg, paddingBottom: spacing.xl },
    title: {
      fontSize: 24,
      fontWeight: "600",
      color: colors.text,
      marginBottom: spacing.md,
    },
    backButton: {
      flexDirection: "row",
      alignItems: "center",
      marginBottom: spacing.md,
    },
    filterRow: {
      flexDirection: "row",
      gap: spacing.sm,
      marginBottom: spacing.md,
    },
    filterButton: {
      flex: 1,
      borderWidth: 1,
      borderColor: colors.border,
      borderRadius: 8,
      padding: spacing.sm,
      alignItems: "center",
    },
    filterText: { fontSize: 14, color: colors.text },
    inputContainer: { marginBottom: spacing.md },
    buttonRow: { marginTop: spacing.lg },
    description: {
      color: colors.textLight,
      marginBottom: spacing.md,
    },
  });

  return (
    <View style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === "ios" ? "padding" : "height"}
        style={styles.keyboardAvoid}
      >
        <ScrollView
          contentContainerStyle={styles.scrollContent}
          keyboardShouldPersistTaps="handled"
        >
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => router.back()}
          >
            <Ionicons name="arrow-back" size={24} color={colors.primary} />
            <Text style={{ color: colors.primary, marginLeft: 4 }}>Back</Text>
          </TouchableOpacity>

          <Text style={styles.title}>Stock Addition Report</Text>

          <Text style={styles.description}>
            Download a PDF report of all stock‑in transactions within the
            selected date range.
          </Text>

          <View style={styles.filterRow}>
            <DatePicker
              value={startDate}
              onChange={setStartDate}
              placeholder="Start Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            <DatePicker
              value={endDate}
              onChange={setEndDate}
              placeholder="End Date"
              containerStyle={{ flex: 1, marginBottom: 0 }}
            />
            {(startDate || endDate) && (
              <TouchableOpacity
                style={[styles.filterButton, { borderColor: colors.danger }]}
                onPress={() => {
                  setStartDate("");
                  setEndDate("");
                }}
              >
                <Text style={{ color: colors.danger }}>Clear</Text>
              </TouchableOpacity>
            )}
          </View>

          <SearchableSelect
            label="Product (optional)"
            placeholder="All products"
            options={products.map((p) => ({
              label: `${p.name} (${p.sku})`,
              value: p.id.toString(),
            }))}
            value={selectedProductId}
            onValueChange={setSelectedProductId}
            loading={productsLoading}
            containerStyle={styles.inputContainer}
            clearable
          />

          <View style={styles.buttonRow}>
            <Button
              title="Download PDF"
              onPress={handleDownloadPdf}
              loading={downloading}
              variant="primary"
            />
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
}
